import { ALL_ROLES, type AppUser } from '../data/types';

/**
 * The last profile (role + scope) the server gave each account, kept in
 * this browser so the app opens straight away on the next visit while
 * /api/me re-checks in the background (owner ruling 2026-10-20 — "Loading
 * your profile" took ~10 s). NOT a permission: the server re-applies every
 * role on every request, so a stale copy can only show a screen whose data
 * the server then refuses; the fresh answer replaces it within seconds.
 * Profile only — never audit data. Per account (shared devices), dropped
 * at sign-out, and ignored after MAX_AGE_MS.
 */

const PREFIX = 'sspaudit.profile.';
export const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export interface CachedProfile {
  user: AppUser;
  groupsOverage: boolean;
  /**
   * Districts the server summarizes for this account ('*' = all; trimmed +
   * lower-case names) — /api/me's `summaryDistricts`. Optional: older saved
   * copies lack it (then pages read audits until /api/me answers). Not a
   * permission either: the server only returns rows the account sees.
   */
  summaryDistricts?: '*' | string[];
}

/** '*' or a list of names (≤ 500), else undefined. */
export function asSummaryDistricts(v: unknown): '*' | string[] | undefined {
  if (v === '*') return '*';
  return isStringList(v) && v.length <= 500 ? v.map((d) => d.trim().toLowerCase()) : undefined;
}

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function storage(): Store | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null; // blocked storage: no cache, the app just waits for /api/me
  }
}

const sameAddress = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

const keyFor = (username: string) => PREFIX + username.trim().toLowerCase();

const isStringList = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string');

/** A well-formed AppUser, or null (hand-edited or older-shaped entries are ignored). */
function asUser(v: unknown): AppUser | null {
  if (!v || typeof v !== 'object') return null;
  const u = v as Record<string, unknown>;
  if (
    typeof u.upn !== 'string' ||
    u.upn === '' ||
    typeof u.displayName !== 'string' ||
    !ALL_ROLES.includes(u.role as AppUser['role']) ||
    !isStringList(u.districts) ||
    !isStringList(u.storeNumbers)
  ) {
    return null;
  }
  return {
    upn: u.upn,
    displayName: u.displayName,
    role: u.role as AppUser['role'],
    districts: u.districts,
    storeNumbers: u.storeNumbers,
  };
}

export function readCachedProfile(username: string, now = Date.now(), store = storage()): CachedProfile | null {
  if (!store || !username.trim()) return null;
  try {
    const raw = store.getItem(keyFor(username));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at?: unknown; groupsOverage?: unknown; user?: unknown; summaryDistricts?: unknown };
    const user = asUser(parsed.user);
    if (!user || typeof parsed.at !== 'number' || now - parsed.at > MAX_AGE_MS || parsed.at > now + 60_000) {
      return null;
    }
    // Only ever this account's own profile (two accounts in one browser
    // must never open as each other — the sync engine follows this upn).
    if (!sameAddress(user.upn, username)) return null;
    const summaryDistricts = asSummaryDistricts(parsed.summaryDistricts);
    return { user, groupsOverage: parsed.groupsOverage === true, ...(summaryDistricts ? { summaryDistricts } : {}) };
  } catch {
    return null;
  }
}

let signingOut = false;

/** Sign-out has begun: a /api/me answer still in flight must not save a copy back. */
export function stopSavingProfiles(): void {
  signingOut = true;
}

export function writeCachedProfile(
  username: string,
  profile: CachedProfile,
  now = Date.now(),
  store = storage(),
): void {
  // Saved only under the account it belongs to.
  if (signingOut || !store || !username.trim() || !sameAddress(profile.user.upn, username)) return;
  try {
    store.setItem(keyFor(username), JSON.stringify({ at: now, ...profile }));
  } catch {
    // full or blocked: the next visit just waits for the server
  }
}

export function forgetCachedProfile(username: string, store = storage()): void {
  if (!store || !username.trim()) return;
  try {
    store.removeItem(keyFor(username));
  } catch {
    // blocked storage: nothing was kept
  }
}

/** Same role, scope and names (order-sensitive — the server answers in a stable order). */
export function sameProfile(a: AppUser, b: AppUser): boolean {
  return (
    a.upn === b.upn &&
    a.displayName === b.displayName &&
    a.role === b.role &&
    a.districts.join('\u0000') === b.districts.join('\u0000') &&
    a.storeNumbers.join('\u0000') === b.storeNumbers.join('\u0000')
  );
}
